import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Menu, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useBag } from "@/context/BagContext";
import { LOGO_PNG, LOGO_ASPECT } from "@/lib/logo";

const LANGS = ["fr", "en", "ar"];

export default function Header() {
  const { t, lang, setLang } = useLang();
  const { count } = useBag();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { to: "/boutique", label: t.nav.shop, id: "nav-link-shop" },
    { to: "/a-propos", label: t.nav.about, id: "nav-link-about" },
    { to: "/contact", label: t.nav.contact, id: "nav-link-contact" },
  ];

  const LangSwitch = ({ testid, size = "sm" }) => (
    <div className="flex items-center gap-1" data-testid={testid}>
      {LANGS.map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`uppercase transition-colors duration-200 ${
            size === "lg" ? "text-[13px] px-2 py-1" : "text-[11px] px-1.5 py-0.5 tracking-[0.08em]"
          } ${lang === l ? "text-rose-deep font-semibold" : "text-ink/55 hover:text-ink"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div
        className="bg-rose/20 text-ink/70 px-4 py-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-0.5"
        data-testid="announcement-bar"
      >
        <p className="text-[10.5px] uppercase tracking-[0.18em] text-center">{t.announce}</p>
        <LangSwitch testid="lang-switcher-top" />
      </div>

      <header className="sticky top-0 z-50 bg-ivory/85 backdrop-blur-md border-b border-ink/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 h-[72px] grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="flex items-center gap-6">
            <button
              className="lg:hidden p-2 -ms-2 text-ink"
              onClick={() => setOpen(true)}
              aria-label={t.common.menu}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={it.id}
                  className={({ isActive }) =>
                    `text-[12px] uppercase tracking-[0.16em] transition-colors duration-200 hover:text-rose-deep ${
                      isActive || (it.to === "/boutique" && location.pathname.startsWith("/piece")) ? "text-rose-deep" : "text-ink/75"
                    }`
                  }
                >
                  {it.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <Link to="/" data-testid="header-logo-link" className="flex items-center justify-center">
            <img src={LOGO_PNG} alt="MIORA Boutique" style={{ height: 38, aspectRatio: LOGO_ASPECT }} />
          </Link>

          <div className="flex items-center justify-end gap-3.5 sm:gap-5">
            <button
              onClick={() => toast(t.common.wishlistSoon)}
              data-testid="header-wishlist-button"
              aria-label="Wishlist"
              className="hidden sm:block p-2 text-ink hover:text-rose-deep transition-colors"
            >
              <Heart className="w-[19px] h-[19px]" strokeWidth={1.5} />
            </button>
            <Link
              to="/panier"
              data-testid="header-bag-link"
              className="relative p-2 text-ink hover:text-rose-deep transition-colors"
              aria-label={t.nav.bag}
            >
              <ShoppingBag className="w-[19px] h-[19px]" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="bag-count-badge"
                  className="absolute -top-0.5 -end-0.5 bg-rose text-ivory text-[9px] w-4 h-4 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] bg-ivory"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex items-center justify-between px-5 h-[72px] border-b border-ink/10">
              <img src={LOGO_PNG} alt="MIORA Boutique" style={{ height: 34, aspectRatio: LOGO_ASPECT }} />
              <button
                className="p-2 text-ink"
                onClick={() => setOpen(false)}
                aria-label={t.common.close}
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="px-8 pt-14 flex flex-col gap-8">
              {[
                ...navItems,
                { to: "/panier", label: t.nav.bag, id: "mobile-link-bag" },
              ].map((it, i) => (
                <motion.div
                  key={it.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06 }}
                >
                  <Link
                    to={it.to}
                    data-testid={it.id}
                    onClick={() => setOpen(false)}
                    className="font-serif text-4xl text-ink hover:text-rose-deep transition-colors"
                  >
                    {it.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="pt-4"
                data-testid="mobile-lang-switcher"
              >
                <LangSwitch testid="mobile-lang-switcher-inner" size="lg" />
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
